// The Match module's search box (Ben, 2026-10-30: "a way of searching for games, rather than being
// locked on the initial game"). Reads football (live/game-search.ts): a team, two teams (their
// meetings), a score, a month. Empty, it suggests games from what's on the canvas: the latest game
// of each team shown there, a head-to-head when there are two, and the current player's club's
// latest games. A game picked shows here (one Undo step); a game dragged out drops like any other.

import { useEffect, useRef, useState } from "react";
import { SubControls } from "./SubControls.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { setModState } from "./bridge.js";
import { liveApi } from "../live/api.js";
import { findTeams, parseGameQuery, searchGames, searchGamesIn, searchLeagueGames, type FoundTeam, type GameQuery, type SearchedLeagues } from "../live/game-search.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { loadedPlayerIndex, searchTeams } from "../live/player-index.js";
import { loadStickyClub } from "../live/squad.js";
import { gameDate, teamOfModule } from "../live/team.js";
import type { MatchRef } from "../live/match.js";
import { getStickyPlayer } from "../canvas/sticky-player.js";
import { canvasApi } from "../canvas/shell-actions.js";
import { undoApi } from "../canvas/undo.js";
import { startMatchCardDragOnMove } from "../canvas/menu-drag.js";

type Row = { kind: "label"; text: string } | { kind: "game"; match: MatchRef } | { kind: "query"; text: string; query: string };

/** How many games a search lists. */
const MAX_ROWS = 30;

/** The teams the canvas shows (a Bio, a Matches, a Stats tile holding one), each once. */
function canvasTeams(): FoundTeam[] {
  const seen = new Map<number, FoundTeam>();
  for (const mod of document.querySelectorAll(".placed-module")) {
    const t = teamOfModule(mod);
    if (t && !seen.has(t.id)) seen.set(t.id, { id: t.id, name: t.name, leagueId: t.competitionId });
  }
  return [...seen.values()];
}

async function suggestions(): Promise<Row[]> {
  const api = liveApi();
  const rows: Row[] = [];
  const teams = canvasTeams().slice(0, 3);
  for (const t of teams) {
    const [latest] = await searchGames(api, [t], parseGameQuery(""), 2);
    if (latest) rows.push({ kind: "label", text: `Latest · ${t.name}` }, { kind: "game", match: latest });
  }
  if (teams.length >= 2) {
    const [a, b] = teams as [FoundTeam, FoundTeam];
    rows.push({ kind: "query", text: `Every ${a.name} v ${b.name} game`, query: `${a.name} ${b.name}` });
  }
  const sticky = getStickyPlayer();
  if (sticky !== null) {
    const club = await loadStickyClub(api, sticky);
    if (club.teamId !== null && club.competitionId !== null && club.teamName !== null && !teams.some((t) => t.id === club.teamId)) {
      const games = await searchGames(api, [{ id: club.teamId, name: club.teamName, leagueId: club.competitionId }], parseGameQuery(""), 1);
      if (games.length) rows.push({ kind: "label", text: `Latest games · ${club.teamName}` }, ...games.slice(0, 5).map((match) => ({ kind: "game" as const, match })));
    }
  }
  return rows;
}

/** The leagues a score with no team is looked for in: the canvas teams' and the current player's. */
async function contextLeagues(): Promise<number[]> {
  const ids = new Set(canvasTeams().map((t) => t.leagueId));
  const sticky = getStickyPlayer();
  if (sticky !== null) {
    const club = await loadStickyClub(liveApi(), sticky);
    if (club.competitionId !== null) ids.add(club.competitionId);
  }
  return [...ids].slice(0, 3);
}

/** Numbers alone ("5-2", "5"): that score or a side's goals, this season and last, in the
 *  canvas's leagues. */
async function scoreOnly(q: GameQuery, leagueName: (id: number) => string): Promise<{ rows: Row[]; note: string | null }> {
  const leagues = await contextLeagues();
  if (!leagues.length) return { rows: [], note: "Add a team or a player to the canvas, or type a team with the score." };
  const games = await searchLeagueGames(liveApi(), leagues, q, 2);
  const what = q.score ? `${q.score[0]}–${q.score[1]}` : `a side scoring ${q.goals}`;
  if (!games.length) return { rows: [], note: `No ${what} games this season or last in ${leagues.map((id) => `the ${leagueName(id)}`).join(" or ")}.` };
  const title = `${q.score ? `${what} games` : `Games with ${what}`} · this season and last`;
  return { rows: [{ kind: "label", text: title }, ...games.slice(0, MAX_ROWS).map((match) => ({ kind: "game" as const, match }))], note: null };
}

/** "the Premier League (14/15–26/27) or the Championship (21/22–26/27)": what a search looked
 *  through, in words. */
function searchedText(searched: SearchedLeagues, leagueName: (id: number) => string): string {
  const parts = searched.map(({ leagueId, seasons }) => {
    const newest = seasons[0];
    const oldest = seasons[seasons.length - 1];
    const span = !newest || !oldest ? "no season of those years" : newest === oldest ? humanizeSeasonCode(newest) : `${humanizeSeasonCode(oldest)}–${humanizeSeasonCode(newest)}`;
    return `the ${leagueName(leagueId)} (${span})`;
  });
  return parts.join(" or ");
}

/** The games for what's typed, or a line saying why there are none. */
async function results(text: string, leagueName: (id: number) => string): Promise<{ rows: Row[]; note: string | null }> {
  const index = loadedPlayerIndex();
  if (!index) return { rows: [], note: "The search is still loading…" };
  const q = parseGameQuery(text);
  const teams = findTeams(q.teamText, (t) => searchTeams(index, t).map((hit) => ({ id: hit.id, name: hit.name, leagueId: hit.leagueId })));
  if (!teams.length && !q.teamText && (q.score || q.goals !== null)) return scoreOnly(q, leagueName);
  if (!teams.length) return { rows: [], note: q.teamText ? `No team found for "${q.teamText}".` : "Type a team, two teams, a score, a month or a season." };
  // A plain team: its latest two seasons; a head-to-head, a score or a month: every season.
  const plain = teams.length === 1 && !q.score && q.goals === null && q.month === null && q.year === null && q.season === null;
  const { games, searched } = await searchGamesIn(liveApi(), teams, q, plain ? 2 : Infinity);
  if (!games.length) {
    // Never "there were none" (Ben, 2026-10-30): only what was looked through.
    const who = teams.length === 2 ? `${teams[0]!.name} v ${teams[1]!.name}` : teams[0]!.name;
    return { rows: [], note: `No ${who} games found in ${searchedText(searched, leagueName)}. Cups aren't searched yet.` };
  }
  const title = teams.length === 2 ? `${teams[0]!.name} v ${teams[1]!.name}: ${games.length} ${games.length === 1 ? "game" : "games"}` : `${teams[0]!.name}`;
  return { rows: [{ kind: "label", text: title }, ...games.slice(0, MAX_ROWS).map((match) => ({ kind: "game" as const, match }))], note: null };
}

export function MatchSearch({ mod, body }: { mod: HTMLElement; body: HTMLElement }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(-1);
  const leagues = useLiveLeagueChoices();
  // Read through a ref by the search (it runs after a pause, with the newest list).
  const leaguesRef = useRef(leagues);
  leaguesRef.current = leagues;

  useEffect(() => {
    if (!open) return;
    let live = true;
    const timer = setTimeout(() => {
      const leagueName = (id: number) => leaguesRef.current.find((l) => l.id === id)?.label ?? "league";
      const work = text.trim() ? results(text, leagueName) : suggestions().then((r) => ({ rows: r, note: r.length ? null : "Type a team, two teams, a score, a month or a season." }));
      work.then(
        (r) => {
          if (!live) return;
          setRows(r.rows);
          setNote(r.note);
          setHighlight(-1);
        },
        (err: unknown) => {
          console.error("[live] the game search failed:", err);
          if (live) setNote("The search failed. Try again.");
        },
      );
    }, 180);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [text, open]);

  const games = rows.filter((r): r is Extract<Row, { kind: "game" }> => r.kind === "game");
  const pick = (match: MatchRef) => {
    undoApi().saveState();
    setModState(mod, "_match", match);
    setModState(mod, "_matchFor", undefined);
    setOpen(false);
    setText("");
    inputRef.current?.blur();
    canvasApi().refreshModuleBody(mod);
  };

  let gameIndex = -1;
  return (
    <SubControls mod={mod} body={body}>
      <div className="player-search-wrap">
        <input
          ref={inputRef}
          type="text"
          className="player-search"
          placeholder="Search games: a team, two teams, a score…"
          autoComplete="off"
          spellCheck={false}
          value={text}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setText("");
              inputRef.current?.blur();
            } else if (e.key === "Enter") {
              e.preventDefault();
              const g = games[highlight] ?? games[0];
              if (g) pick(g.match);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              if (games.length) setHighlight(Math.min(highlight + 1, games.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              if (games.length) setHighlight(Math.max(highlight - 1, 0));
            }
          }}
          // Delayed so a click on a result lands before the list closes.
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        <div className={open ? "player-search-results open" : "player-search-results"}>
          {note && <div className="picker-empty">{note}</div>}
          {rows.map((r, i) => {
            if (r.kind === "label") {
              return (
                <div key={i} className="player-search-section">
                  {r.text}
                </div>
              );
            }
            if (r.kind === "query") {
              return (
                <div
                  key={i}
                  className="player-search-option"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    setText(r.query);
                  }}
                >
                  {r.text} →
                </div>
              );
            }
            gameIndex += 1;
            const index = gameIndex;
            const m = r.match;
            return (
              <div
                key={i}
                className={`player-search-option match-search-game${index === highlight ? " highlighted" : ""}`}
                onMouseEnter={() => setHighlight(index)}
                onPointerDown={(e) => {
                  // Keep the box open; a move drags the game out, a click picks it.
                  e.preventDefault();
                  if (window.innerWidth > 768 && e.button === 0) startMatchCardDragOnMove(e.nativeEvent, m);
                }}
                onClick={() => pick(m)}
              >
                <span className="match-search-teams">
                  {m.home.name} {m.homeScore}–{m.awayScore} {m.away.name}
                </span>
                <span className="match-search-when">
                  {gameDate(m)} · {humanizeSeasonCode(m.season)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </SubControls>
  );
}
